import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { del, get, patch, post } from "../lib/api";
import { useDocumentTitle, useLoad, listUrl } from "../lib/hooks";
import { channelIcon, channelLabel, diffPatch, formatDay } from "../lib/format";
import { CURRENCIES, LEAD_SOURCES, buildStepper, durationText, formatMoney, leadError, leadName, signalTypeIcon, signalTypeLabel, stageIcon, stageLabel } from "../lib/leads";
import { formatDateTime, toISODate, todayISO } from "../../helpers/datetime";
import { Avatar, Badge, Button, Card, Confirm, ErrorBox, Loading, Menu, MenuItem, Select, Tabs } from "../ui";
import { HiddenLabel } from "../components/Hidden";
import { AutoField } from "../components/DetailParts";
import EntityPicker from "../components/EntityPicker";
import Timeline from "../components/Timeline";
import History from "../components/History";
import TagEditor from "../components/TagEditor";
import NextStepFields from "../components/NextStepFields";
import MoveStageModal from "../components/MoveStageModal";
import { LeadSourceBadge, LeadValue, StageBadge, StallFlag } from "../components/LeadParts";
import { useShell } from "../shell/context";

// PATCH for the lead: only changed keys; 422 rule codes become clear text.
function useLeadPatcher(id, lead, setLead) {
    const { toast, bump } = useShell();
    return async (changes, { quiet } = {}) => {
        const body = diffPatch(lead, changes, Object.keys(changes));
        if (Object.keys(body).length === 0) return true;
        try {
            const res = await patch(`v1/crm/leads/${id}`, body);
            setLead((l) => ({ ...l, ...res }));
            if (!quiet) toast("Saved.");
            bump();
            return true;
        } catch (e) {
            toast(leadError(e).message, { error: true });
            return false;
        }
    };
}

function InlineTitle({ lead, save }) {
    const [value, setValue] = useState(lead.title || "");
    useEffect(() => setValue(lead.title || ""), [lead.title]);
    return (
        <input
            className="hz-inline-name"
            value={value}
            placeholder={lead.company_name}
            aria-label="Lead title"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            onBlur={() => value.trim() !== (lead.title || "") && save({ title: value.trim() })}
        />
    );
}

// A company or contact reference: a link with Change (and Remove when the
// field can be empty), or a picker while editing.
function RefField({ label, kind, id, name, onSave, clearable, missing = "Not set" }) {
    const [editing, setEditing] = useState(false);
    const path = kind === "company" ? "/crm/companies/" : "/crm/contacts/";
    return (
        <>
            <span>{label}</span>
            {editing ? (
                <div className="hz-row" style={{ gap: 6 }}>
                    <div className="hz-spacer">
                        <EntityPicker kind={kind} value={null} autoFocus onChange={(v) => v && onSave(v.id).then((ok) => ok && setEditing(false))} />
                    </div>
                    <Button size="sm" variant="light" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
            ) : (
                <div className="hz-row-wrap" style={{ gap: 8 }}>
                    {id && name ? <Link to={path + id}>{name}</Link> : <span className="hz-muted">{id ? "In the trash" : missing}</span>}
                    <button type="button" className="hz-btn-link" onClick={() => setEditing(true)}>{id ? "Change" : "Set"}</button>
                    {clearable && id && <button type="button" className="hz-btn-link danger" onClick={() => onSave(null)}>Remove</button>}
                </div>
            )}
        </>
    );
}

function Stepper({ lead, stages, onPick }) {
    const [showLog, setShowLog] = useState(false);
    const steps = buildStepper(stages, lead);
    return (
        <Card
            noBody
            title="Stage"
            actions={
                <>
                    <span className="hz-small hz-muted">Opened {formatDay(lead.opened_at, { short: true })}{lead.closed_at ? ` · closed ${formatDay(lead.closed_at, { short: true })}` : ""}</span>
                    <Button size="sm" variant="ghost" icon="ri-history-line" onClick={() => setShowLog((s) => !s)}>{showLog ? "Hide moves" : `${stages.length} ${stages.length === 1 ? "move" : "moves"}`}</Button>
                </>
            }
            style={{ marginBottom: "var(--hz-gutter)" }}
        >
            <div className="hz-stepper">
                {steps.map((s) => {
                    const target = s.stage === "closed" ? "won" : s.stage;
                    const canPick = s.state !== "current";
                    return (
                        <button
                            key={s.stage}
                            type="button"
                            className={`hz-step ${s.state} ${s.stage}`}
                            disabled={!canPick}
                            title={canPick ? `Move to ${s.stage === "closed" ? "Won or Lost" : s.label}` : "Current stage"}
                            onClick={() => canPick && onPick(target)}
                        >
                            <span className="hz-step-dot">
                                <i className={s.state === "done" ? "ri-check-line" : s.stage === "closed" ? "ri-flag-2-line" : stageIcon(s.stage)}></i>
                            </span>
                            <span className="hz-step-label">{s.label}</span>
                            <span className="hz-step-meta">
                                {s.state === "skipped" && "Skipped"}
                                {s.days !== null && (
                                    <>
                                        {s.state === "current" && lead.is_open ? `${durationText(s.days)} so far` : s.stage === "won" || s.stage === "lost" ? "" : durationText(s.days)}
                                        {s.visits > 1 ? ` · ${s.visits} visits` : ""}
                                        <br />
                                        {s.enteredAt && `${s.state === "current" ? "since" : "entered"} ${formatDay(toISODate(s.enteredAt), { short: true })}`}
                                    </>
                                )}
                            </span>
                        </button>
                    );
                })}
            </div>
            {showLog && (
                <div style={{ borderTop: "1px solid var(--hz-border)" }}>
                    {stages.map((h) => (
                        <div key={h.id} className="hz-list-item hz-small" style={{ padding: "8px 16px" }}>
                            <i className={stageIcon(h.to_stage)} style={{ color: "var(--hz-primary)" }}></i>
                            <span className="hz-spacer">
                                {h.from_stage ? `${stageLabel(h.from_stage)} → ${stageLabel(h.to_stage)}` : `Created in ${stageLabel(h.to_stage)}`}
                                <span className="hz-muted"> · {durationText(h.days)}{h.is_current ? " so far" : ""}</span>
                            </span>
                            <span className="hz-muted">{h.source}</span>
                            <span className="hz-muted hz-nowrap">{formatDateTime(h.changed_at)}</span>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

function NextStepCard({ lead, onSaved }) {
    const { toast, bump } = useShell();
    const [step, setStep] = useState(lead.next_step || "");
    const [date, setDate] = useState(lead.next_step_date || "");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        setStep(lead.next_step || "");
        setDate(lead.next_step_date || "");
        setError(null);
    }, [lead.next_step, lead.next_step_date]);
    const changed = step.trim() !== (lead.next_step || "") || date !== (lead.next_step_date || "");
    const save = async () => {
        if (!step.trim() || !date) return setError({ message: "An open lead needs a next step and a date for it.", field: "next_step" });
        setBusy(true);
        try {
            const res = await patch(`v1/crm/leads/${lead.id}`, { next_step: step.trim(), next_step_date: date });
            toast("Next step saved.");
            bump();
            setError(null);
            onSaved(res);
        } catch (e) {
            setError(leadError(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Card title="Next step" actions={<StallFlag lead={lead} today={todayISO()} withDetail />}>
            <div className="hz-form-grid" style={{ gap: 10 }}>
                {error && <div className="hz-alert hz-alert-danger">{error.message}</div>}
                <NextStepFields
                    label="What happens next"
                    step={step}
                    date={date}
                    onStep={(e) => setStep(e.target.value)}
                    onDate={(v) => setDate(v && v.target ? v.target.value : v)}
                    invalid={error && error.field === "next_step"}
                    from={todayISO()}
                />
                {changed && (
                    <div className="hz-row">
                        <Button size="sm" variant="primary" disabled={busy} onClick={save}>Save next step</Button>
                        <Button size="sm" variant="light" onClick={() => { setStep(lead.next_step || ""); setDate(lead.next_step_date || ""); setError(null); }}>Cancel</Button>
                    </div>
                )}
            </div>
        </Card>
    );
}

function Origin({ lead }) {
    const t = lead.origin_touch;
    const s = lead.origin_signal;
    let body;
    if (t) {
        const who = t.contact_id ? (
            <Link to={`/crm/contacts/${t.contact_id}`}>{t.contact_name || "Contact"}</Link>
        ) : t.company_id ? (
            <Link to={`/crm/companies/${t.company_id}`}>{t.company_name || "Company"}</Link>
        ) : null;
        body = (
            <div className="hz-origin">
                <i className={channelIcon(t.channel)}></i>
                <div className="hz-spacer" style={{ minWidth: 0 }}>
                    <div>
                        <span className="hz-strong">Touch</span>: {channelLabel(t.channel)}{who && <> with {who}</>} · {formatDay(t.touch_date, { short: true })}
                    </div>
                    {t.summary && <div className="hz-small" style={{ color: "var(--hz-gray-700)" }}>{t.summary}</div>}
                    {t.on_their_mind && <div className="hz-small hz-muted">On their mind: {t.on_their_mind}</div>}
                </div>
            </div>
        );
    } else if (s) {
        body = (
            <div className="hz-origin">
                <i className={signalTypeIcon(s.type)}></i>
                <div className="hz-spacer" style={{ minWidth: 0 }}>
                    <div>
                        <span className="hz-strong">Signal</span>: {signalTypeLabel(s.type)} · seen {formatDay(s.seen_on, { short: true })}
                    </div>
                    {s.detail && <div className="hz-small" style={{ color: "var(--hz-gray-700)" }}>{s.detail}</div>}
                    <div className="hz-row-wrap hz-small" style={{ gap: 10, marginTop: 2 }}>
                        <Link to={`/crm/signals?company_id=${s.company_id}&highlight=${s.id}`}>Open in Signals</Link>
                        {s.source_url && <a href={s.source_url} target="_blank" rel="noopener noreferrer">Source</a>}
                    </div>
                </div>
            </div>
        );
    } else if (lead.origin_touch_id || lead.origin_signal_id) {
        body = <span className="hz-muted">The {lead.origin_touch_id ? "touch" : "signal"} that started this lead is in the trash.</span>;
    } else {
        body = <span className="hz-muted">Not started from a touch or a signal.</span>;
    }
    return <Card title="Origin">{body}</Card>;
}

function Details({ lead, save }) {
    const cur = (lead.currency || "EUR").toUpperCase();
    const currencies = CURRENCIES.includes(cur) ? CURRENCIES : [...CURRENCIES, cur];
    return (
        <Card title="Details" actions={<span className="hz-xs hz-muted"><i className="ri-check-line"></i> Saves when you leave a field</span>}>
            <div className="hz-form-grid">
                <div className="hz-kv">
                    <RefField label="Company" kind="company" id={lead.company_id} name={lead.company_name} onSave={(v) => save({ company_id: v })} />
                    <RefField label="Primary contact" kind="contact" id={lead.primary_contact_id} name={lead.primary_contact_name} clearable onSave={(v) => save({ primary_contact_id: v })} />
                    <RefField label="Introduced by" kind="contact" id={lead.introduced_by_contact_id} name={lead.introduced_by_name} clearable missing="Nobody" onSave={(v) => save({ introduced_by_contact_id: v })} />
                    <span>Source</span>
                    <Select small value={lead.source} onChange={(e) => save({ source: e.target.value })} options={LEAD_SOURCES} style={{ maxWidth: 200 }} aria-label="Source" />
                    <span>Created by</span>
                    <span className="hz-small">{lead.source_channel === "claude" ? "Claude" : lead.source_channel === "import" ? "An import" : "Hand entry"}</span>
                </div>
                <div className="hz-form-2" style={{ gridTemplateColumns: cur !== "EUR" ? "1fr 100px 1fr" : "1fr 100px" }}>
                    <AutoField label="Estimated value" field="est_value" type="number" entity={lead} save={save} placeholder="Not set" />
                    <div>
                        <span className="hz-label">Currency</span>
                        <Select value={cur} onChange={(e) => save({ currency: e.target.value })} options={currencies} aria-label="Currency" />
                    </div>
                    {cur !== "EUR" && <AutoField label={`1 ${cur} in EUR`} field="fx_rate_to_eur" type="number" entity={lead} save={save} placeholder="From settings" />}
                </div>
                {cur !== "EUR" && lead.est_value !== null && (
                    <div className="hz-hint" style={{ marginTop: -8 }}>
                        {lead.value_eur !== null ? `About ${formatMoney(lead.value_eur, "EUR")}.` : "EUR value unknown: set a rate here, or the fx_rate_to_eur_" + cur + " setting."}
                        {!lead.is_open && " The rate is frozen now that the lead is closed."}
                    </div>
                )}
                <div className="hz-form-2">
                    <AutoField label="Opened on" field="opened_at" type="date" entity={lead} save={save} />
                    {!lead.is_open && <AutoField label="Closed on" field="closed_at" type="date" entity={lead} save={save} />}
                </div>
                {!lead.is_open && <AutoField label={lead.stage === "won" ? "Why it was won" : "Why it was lost"} field="outcome_reason" entity={lead} save={save} multiline rows={2} />}
                <AutoField label="Notes" field="notes" entity={lead} save={save} multiline />
            </div>
        </Card>
    );
}

export default function LeadDetail() {
    const { id } = useParams();
    const history = useHistory();
    const { version, openLog, toast, bump } = useShell();
    const [tab, setTab] = useState("touches");
    const [move, setMove] = useState(null); // target stage or "" for the picker
    const [confirmDelete, setConfirmDelete] = useState(false);
    const { data: lead, error, loading, reload, setData } = useLoad(() => get(`v1/crm/leads/${id}`), [id, version]);
    const stages = useLoad(() => get(`v1/crm/leads/${id}/stages`), [id, version]);
    const save = useLeadPatcher(id, lead || {}, setData);
    useDocumentTitle(lead ? leadName(lead) : "Lead");

    if (loading && !lead) return <Loading />;
    if (error && !lead) return <ErrorBox error={error} onRetry={reload} />;
    if (!lead) return null;
    const today = todayISO();

    const remove = async () => {
        try {
            await del(`v1/crm/leads/${id}`);
            setConfirmDelete(false);
            toast(`${leadName(lead)} moved to the trash.`, {
                icon: "ri-delete-bin-line",
                action: { label: "Undo", run: () => post(`v1/crm/trash/leads/${id}/restore`).then(() => { bump(); history.push(`/crm/leads/${id}`); }).catch((e) => toast(e.message, { error: true })) },
            });
            bump();
            history.push("/crm/leads");
        } catch (e) {
            setConfirmDelete(false);
            toast(e.message, { error: true });
        }
    };

    const logPrefill = {
        contact: lead.primary_contact_id ? { id: lead.primary_contact_id, name: lead.primary_contact_name || "Contact" } : null,
        company: { id: lead.company_id, name: lead.company_name },
    };

    return (
        <>
            <Link to={listUrl("/crm/leads")} className="hz-back"><i className="ri-arrow-left-s-line"></i>Leads</Link>
            <div className="hz-detail-head">
                <Avatar name={lead.company_name} large />
                <div className="hz-detail-id">
                    <InlineTitle lead={lead} save={save} />
                    <div style={{ color: "var(--hz-slate-700)" }}>
                        <Link to={`/crm/companies/${lead.company_id}`}>{lead.company_name}</Link> <HiddenLabel at={lead.company_hidden_at} />
                        {lead.primary_contact_name && (
                            <>
                                {" · "}
                                <Link to={`/crm/contacts/${lead.primary_contact_id}`}>{lead.primary_contact_name}</Link> <HiddenLabel at={lead.primary_contact_hidden_at} />
                            </>
                        )}
                        {lead.introduced_by_name && (
                            <span className="hz-muted">
                                {" · introduced by "}
                                <Link to={`/crm/contacts/${lead.introduced_by_contact_id}`}>{lead.introduced_by_name}</Link> <HiddenLabel at={lead.introduced_by_hidden_at} />
                            </span>
                        )}
                    </div>
                    <div className="hz-detail-meta">
                        <StageBadge stage={lead.stage} />
                        <StallFlag lead={lead} today={today} withDetail />
                        <LeadSourceBadge source={lead.source} />
                        {lead.is_open && lead.days_in_stage !== null && <span className="hz-small hz-muted">{durationText(lead.days_in_stage)} in {stageLabel(lead.stage)}</span>}
                        {!lead.is_open && <Badge color={lead.stage === "won" ? "success" : "secondary"} soft={false}>Closed {formatDay(lead.closed_at, { short: true })}</Badge>}
                    </div>
                </div>
                <div className="hz-detail-status" style={{ display: "block" }}>
                    <div className="hz-small hz-muted">Estimated value</div>
                    <div style={{ fontSize: 17 }}><LeadValue lead={lead} empty={<span className="hz-muted">Not set</span>} /></div>
                </div>
                <div className="hz-row">
                    <Button variant="primary" icon="ri-arrow-right-line" onClick={() => setMove("")}>Move stage</Button>
                    <Button variant="outline" icon="ri-chat-new-line" onClick={() => openLog(logPrefill)}>Log touchpoint</Button>
                    <Menu trigger={(open, toggle) => <Button variant="outline" iconOnly icon="ri-more-2-fill" title="More" onClick={toggle} style={{ width: 37, height: 37 }} />}>
                        <MenuItem danger onClick={() => setConfirmDelete(true)}>Delete lead</MenuItem>
                    </Menu>
                </div>
            </div>

            <Stepper lead={lead} stages={(stages.data && stages.data.data) || lead.stages || []} onPick={(s) => setMove(s)} />

            <div className="hz-detail-cols">
                <div className="hz-detail-left">
                    {lead.is_open ? (
                        <NextStepCard lead={lead} onSaved={(res) => setData((l) => ({ ...l, ...res }))} />
                    ) : (
                        <Card title={lead.stage === "won" ? "Won" : "Lost"} actions={<Button size="sm" variant="ghost" onClick={() => setMove("spotted")}>Reopen</Button>}>
                            {lead.outcome_reason || <span className="hz-muted">No reason given.</span>}
                        </Card>
                    )}
                    <Details lead={lead} save={save} />
                    <Origin lead={lead} />
                    <Card title="Tags">
                        <TagEditor entity="leads" id={lead.id} tags={lead.tags || []} onChange={(tags) => { setData((x) => ({ ...x, tags })); bump(); }} />
                    </Card>
                </div>
                <div className="hz-detail-right">
                    <Card noBody>
                        <Tabs
                            active={tab}
                            onChange={setTab}
                            tabs={[
                                { key: "touches", label: "Touches", icon: "ri-chat-history-line" },
                                { key: "history", label: "History", icon: "ri-history-line" },
                            ]}
                        />
                        {tab === "touches" ? (
                            <>
                                <div className="hz-card-section hz-row" style={{ background: "var(--hz-surface-subtle)" }}>
                                    <span className="hz-small hz-muted hz-spacer">Touches with {lead.company_name} and its people{lead.primary_contact_name ? `, and with ${lead.primary_contact_name}` : ""}.</span>
                                    <Button size="sm" variant="soft" icon="ri-chat-new-line" onClick={() => openLog(logPrefill)}>Log a touch</Button>
                                </div>
                                <Timeline path={`v1/crm/leads/${lead.id}/touches`} version={version} noStartLead />
                            </>
                        ) : (
                            <History entityType="lead" entityId={lead.id} version={version} />
                        )}
                    </Card>
                </div>
            </div>

            {move !== null && <MoveStageModal lead={lead} to={move || null} onClose={() => setMove(null)} onMoved={(res) => setData((l) => ({ ...l, ...res }))} />}
            <Confirm open={confirmDelete} title="Delete lead" danger confirmLabel="Delete" onConfirm={remove} onClose={() => setConfirmDelete(false)}>
                {leadName(lead)} goes to the trash with its stage history and tags. Restore it from Settings, Trash within 30 days.
            </Confirm>
        </>
    );
}
